import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { House, FolderSimple, SignOut, Blueprint, DownloadSimple, X, Monitor, DeviceMobile, UsersThree, Receipt } from '@phosphor-icons/react';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: House },
  { to: '/projects', label: 'Projects', icon: FolderSimple },
  { to: '/billing', label: 'Billing', icon: Receipt },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));

    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setIsInstalled(true);
      }
      setInstallPrompt(null);
    } else {
      setShowGuide(true);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <aside className="w-64 bg-white border-r border-black/15 flex flex-col h-full shrink-0" data-testid="sidebar">
      <div className="p-6 border-b border-black/15">
        <div className="flex items-center gap-2">
          <Blueprint size={28} weight="bold" className="text-[#FF5A00]" />
          <span className="font-sans text-xl font-black tracking-tight">DHR</span>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8F8F8F] mt-1">Platform</p>
      </div>

      <nav className="flex-1 py-4">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3 font-mono text-sm uppercase tracking-wide transition-colors duration-150 ${
                isActive
                  ? 'bg-[#F7F7F7] text-[#FF5A00] border-r-2 border-[#FF5A00]'
                  : 'text-[#5E5E5E] hover:text-black hover:bg-[#F7F7F7]'
              }`
            }
          >
            <Icon size={20} weight="regular" />
            {label}
          </NavLink>
        ))}
        {/* Admin link - only for admin users */}
        {user?.role === 'admin' && (
          <NavLink
            to="/admin"
            data-testid="nav-admin"
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3 font-mono text-sm uppercase tracking-wide transition-colors duration-150 ${
                isActive
                  ? 'bg-[#F7F7F7] text-[#FF5A00] border-r-2 border-[#FF5A00]'
                  : 'text-[#5E5E5E] hover:text-black hover:bg-[#F7F7F7]'
              }`
            }
          >
            <UsersThree size={20} weight="regular" />
            Admin
          </NavLink>
        )}
      </nav>

      <div className="border-t border-black/15 p-4">
        {!isInstalled && (
          <button
            onClick={handleInstall}
            data-testid="install-app-btn"
            className="flex items-center gap-2 w-full px-3 py-2.5 mb-3 bg-[#FF5A00] text-white font-mono text-xs uppercase tracking-wide hover:bg-[#E04E00] transition-all duration-150 shadow-[3px_3px_0px_rgba(0,0,0,1)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
          >
            <DownloadSimple size={16} weight="bold" />
            Install App
          </button>
        )}
        {user && (
          <div className="flex items-center gap-3 mb-3">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 object-cover border border-black/15" />
            ) : (
              <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono text-xs uppercase">
                {user.name?.[0] || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-mono text-xs font-medium truncate">{user.name}</p>
              <p className="font-mono text-[10px] text-[#8F8F8F] truncate">{user.email}</p>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-2 w-full px-3 py-2 font-mono text-xs uppercase tracking-wide text-[#5E5E5E] hover:text-[#FF2B2B] hover:bg-red-50 transition-colors"
        >
          <SignOut size={16} weight="regular" />
          Logout
        </button>
      </div>

      {/* Install Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="install-guide-modal">
          <div className="bg-white border border-black/15 w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-black/15">
              <h3 className="font-sans text-lg font-bold tracking-tight">Install DHR Platform</h3>
              <button onClick={() => setShowGuide(false)} data-testid="close-guide-btn" className="text-[#8F8F8F] hover:text-black">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <div className="flex items-start gap-3 p-4 bg-[#F7F7F7] border border-black/10">
                <Monitor size={24} weight="regular" className="text-[#FF5A00] shrink-0 mt-0.5" />
                <div>
                  <p className="font-sans text-sm font-bold mb-1">Chrome / Edge (Desktop)</p>
                  <ol className="font-mono text-xs text-[#5E5E5E] space-y-1.5 list-decimal list-inside">
                    <li>Click the <strong>install icon</strong> in the address bar (right side)</li>
                    <li>Or click <strong>Menu (&#8942;)</strong> &rarr; <strong>"Install DHR Platform"</strong></li>
                    <li>Click <strong>Install</strong> to confirm</li>
                  </ol>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#F7F7F7] border border-black/10">
                <DeviceMobile size={24} weight="regular" className="text-[#FF5A00] shrink-0 mt-0.5" />
                <div>
                  <p className="font-sans text-sm font-bold mb-1">Mobile (iOS / Android)</p>
                  <ol className="font-mono text-xs text-[#5E5E5E] space-y-1.5 list-decimal list-inside">
                    <li><strong>Android:</strong> Chrome &rarr; Menu (&#8942;) &rarr; "Add to Home Screen"</li>
                    <li><strong>iPhone:</strong> Safari &rarr; Share button &rarr; "Add to Home Screen"</li>
                  </ol>
                </div>
              </div>
              <p className="font-mono text-[10px] text-[#8F8F8F] text-center">The app will open as a standalone window like a native application</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
